import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { Col, Form, Row, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faFingerprint, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

const DocumentoRelacionado = ({
    register,
    getValues,
    setValue,
    errors,
    selectedFacturas,
    setSelectedFacturas,
    impuestosFacturas,
    onAddFactura,
    hasFacturasPPD
}) => {
    
    // Estado local para forzar el re-render y mostrar advertencias sin usar watch
    const [saldosDocumentos, setSaldosDocumentos] = useState({});

    const handleSaldosChange = (index) => {
        // Obtenemos los valores en tiempo real cuando el usuario escribe
        const saldoAnterior = parseFloat(getValues(`doctosRelacionados.${index}.dSaldoAnterior`)) || 0;
        const importePago = parseFloat(getValues(`doctosRelacionados.${index}.dImportePago`)) || 0;
        const montoGlobal = parseFloat(getValues('dMonto')) || 0;
        
        // Calculamos saldo insoluto
        const saldoInsoluto = (saldoAnterior - importePago).toFixed(2);
        
        // Actualizamos el formulario
        setValue(`doctosRelacionados.${index}.dSaldoInsoluto`, saldoInsoluto);
        
        // Guardamos en el estado local para re-renderizar las advertencias
        setSaldosDocumentos(prev => ({
            ...prev,
            [index]: {
                saldoAnterior,
                importePago,
                saldoInsoluto: parseFloat(saldoInsoluto),
                montoGlobal
            }
        }));
    };

    // Auto-calculate vObjetoImpDR based on taxes and sync to form
    useEffect(() => {
        if (selectedFacturas && Array.isArray(selectedFacturas)) {
            selectedFacturas.forEach((factura, index) => {
                const impuestosFactura = (impuestosFacturas && factura.vUUIDFactura) ? impuestosFacturas[factura.vUUIDFactura] : null;
                const traslados = impuestosFactura?.TrasladosDR || [];
                const retenidos = impuestosFactura?.RetencionesDR || [];
                const hasTaxes = traslados.length > 0 || retenidos.length > 0;
                
                const expectedValue = hasTaxes ? '02' : '01';
                const currentValue = getValues(`doctosRelacionados.${index}.vObjetoImpDR`);
                
                if (currentValue !== expectedValue) {
                    setValue(`doctosRelacionados.${index}.vObjetoImpDR`, expectedValue);
                }

                // Autocompletar Saldo Anterior
                if (!getValues(`doctosRelacionados.${index}.dSaldoAnterior`)) {
                    const total = Number(factura?.dTotal) || 0;
                    const pagado = Number(factura?.dTotalPagado) || 0;
                    const saldoAnterior = Math.max(0, total - pagado);
                    setValue(`doctosRelacionados.${index}.dSaldoAnterior`, saldoAnterior.toFixed(2));
                    
                    // Asegurarnos de que el estado local de saldos refleje el cambio inicial
                    setSaldosDocumentos(prev => ({
                        ...prev,
                        [index]: { ...prev[index], saldoAnterior }
                    }));
                }

                // Autocompletar Parcialidad
                if (!getValues(`doctosRelacionados.${index}.iNumeroParcialidad`)) {
                    const pagosPrevios = Number(factura?.iTotalPagos) || 0;
                    setValue(`doctosRelacionados.${index}.iNumeroParcialidad`, pagosPrevios + 1);
                }
            });
        }
    }, [selectedFacturas, impuestosFacturas, setValue, getValues]);

    // Función para abrir la factura en nueva pestaña
    const handleOpenInvoice = (factura) => {
        if (factura?.idFacturaEmitida) {
            const url = `/contabilidad/centro-xml/facturas-emitidas/visualizacion/${factura.idFacturaEmitida}`;
            window.open(url, '_blank');
        }
    };

    const handleRemoveFactura = (indexToRemove) => {
        // Obtenemos los valores actuales de RHF para todos los documentos
        const currentDocs = getValues('doctosRelacionados') || [];
        
        // Removemos el documento del array de RHF
        const newDocs = currentDocs.filter((_, i) => i !== indexToRemove);
        
        // Sobreescribimos el array en RHF para que se recorran los valores
        setValue('doctosRelacionados', newDocs);
        
        // Actualizamos el estado local de saldos para recorrer los índices
        setSaldosDocumentos(prev => {
            const newSaldos = {};
            let newIndex = 0;
            Object.keys(prev).forEach(key => {
                const i = parseInt(key);
                if (i !== indexToRemove) {
                    newSaldos[newIndex] = prev[i];
                    newIndex++;
                }
            });
            return newSaldos;
        });

        // Finalmente quitamos la factura del array de React
        setSelectedFacturas(prev => prev.filter((_, i) => i !== indexToRemove));
    };

    const currentMontoGlobal = parseFloat(getValues('dMonto')) || 0;
    let totalImportesPPD = 0;
    if (selectedFacturas && selectedFacturas.length > 0) {
        selectedFacturas.forEach((_, idx) => {
            const saldos = saldosDocumentos[idx] || {};
            const imp = saldos.importePago ?? (parseFloat(getValues(`doctosRelacionados.${idx}.dImportePago`)) || 0);
            totalImportesPPD += imp;
        });
    }

    return (
        <>
            <Row className="align-items-center mt-3 mb-3 bg-light p-2 rounded">
                <Col>
                    <h5 className="m-0 text-dark">Documentos Relacionados</h5>
                </Col>
                <Col xs="auto">
                    {hasFacturasPPD && (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={onAddFactura}
                        >
                            Seleccionar Factura PPD
                        </Button>
                    )}
                </Col>
            </Row>

            {selectedFacturas.length === 0 ? (
                <div className="text-center p-3 text-muted">
                    <p>No se han seleccionado documentos relacionados. Haga clic en "Seleccionar Factura PPD" para agregar uno.</p>
                </div>
            ) : (
                selectedFacturas.map((factura, index) => {
                    const registerField = (name = '') => register && typeof register === 'function' ? register(`doctosRelacionados.${index}.${name}`) : {};
                    
                    const errSaldoAnterior = errors[`doctosRelacionados.${index}.dSaldoAnterior`];
                    const errImportePago = errors[`doctosRelacionados.${index}.dImportePago`];
                    const errSaldoInsoluto = errors[`doctosRelacionados.${index}.dSaldoInsoluto`];
                    const errNumeroParcialidad = errors[`doctosRelacionados.${index}.iNumeroParcialidad`];
                    const errObjetoImpDR = errors[`doctosRelacionados.${index}.vObjetoImpDR`];
                    const errEquivalenciaDR = errors[`doctosRelacionados.${index}.dEquivalenciaDR`];

                    const saldos = saldosDocumentos[index] || {};
                    // Tomamos del estado local si existe (reactivo al escribir), o del form si apenas se renderiza
                    const currentSaldoInsoluto = saldos.saldoInsoluto ?? (parseFloat(getValues(`doctosRelacionados.${index}.dSaldoInsoluto`)) || 0);
                    const currentImportePago = saldos.importePago ?? (parseFloat(getValues(`doctosRelacionados.${index}.dImportePago`)) || 0);
                    const currentSaldoAnterior = saldos.saldoAnterior ?? (parseFloat(getValues(`doctosRelacionados.${index}.dSaldoAnterior`)) || 0);
                    
                    const isSaldoInsolutoNegativo = currentSaldoInsoluto < 0;
                    const isImportePagoMayorMonto = currentMontoGlobal > 0 && totalImportesPPD > currentMontoGlobal;
                    const isSaldoAnteriorMayorTotal = factura?.dTotal && currentSaldoAnterior > parseFloat(factura.dTotal);
                    const currentObjetoImpDR = getValues(`doctosRelacionados.${index}.vObjetoImpDR`);
                    
                    const monedaPago = getValues('vMoneda') || 'MXN';
                    const monedaFactura = factura?.vMoneda || 'MXN';
                    const isDiferenteMoneda = monedaPago !== monedaFactura;

                    const impuestosFactura = (impuestosFacturas && factura.vUUIDFactura) ? impuestosFacturas[factura.vUUIDFactura] : null;
                    const impuestosTrasladados = impuestosFactura?.TrasladosDR || [];
                    const impuestosRetenidos = impuestosFactura?.RetencionesDR || [];

                    // Propiedades del registro interceptadas
                    const dSaldoAnteriorProps = registerField('dSaldoAnterior');
                    const dImportePagoProps = registerField('dImportePago');

                    return (
                        <div key={index} className="mb-4 border p-3 rounded position-relative">
                            <Button 
                                variant="danger" 
                                size="sm" 
                                className="position-absolute" 
                                style={{ top: '10px', right: '10px' }}
                                onClick={() => handleRemoveFactura(index)}
                                title="Eliminar documento relacionado"
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </Button>
                            
                            <h6 className="mb-3 text-primary">Documento #{index + 1}</h6>

                            <div className="bg-light p-3 rounded border mb-3">
                                {/* Renglón 1: UUID */}
                                <Row className="mb-3">
                                    <Col xs={12}>
                                        <div className="text-muted small">ID Documento (UUID)</div>
                                        <div className="d-flex align-items-center mt-1">
                                            <FontAwesomeIcon icon={faFingerprint} className="text-muted me-2" style={{ fontSize: '0.85rem' }} />
                                            {factura?.idFacturaEmitida ? (
                                                <span 
                                                    className="text-primary"
                                                    style={{ cursor: 'pointer', textDecoration: 'underline', fontSize: '0.85rem', wordBreak: 'break-all' }}
                                                    onClick={() => handleOpenInvoice(factura)}
                                                    title="Abrir factura en nueva pestaña"
                                                >
                                                    {factura?.vUUIDFactura || 'N/A'}
                                                </span>
                                            ) : (
                                                <span style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>{factura?.vUUIDFactura || 'N/A'}</span>
                                            )}
                                        </div>
                                    </Col>
                                </Row>

                                {/* Renglón 2: Método de pago, serie DR, folio DR */}
                                <Row className="g-3 mb-3">
                                    <Col xs={12} md={4}>
                                        <div className="text-muted small">Método de Pago</div>
                                        <div className="fw-bold" style={{ fontSize: '0.9rem' }}>{factura?.vMetodoPago || 'PPD'}</div>
                                    </Col>
                                    <Col xs={6} md={4}>
                                        <div className="text-muted small">Serie DR</div>
                                        <div className="fw-bold" style={{ fontSize: '0.9rem' }}>{factura?.vSerie || '-'}</div>
                                    </Col>
                                    <Col xs={6} md={4}>
                                        <div className="text-muted small">Folio DR</div>
                                        <div className="fw-bold" style={{ fontSize: '0.9rem' }}>{factura?.vFolio || '-'}</div>
                                    </Col>
                                </Row>

                                {/* Renglón 3: Moneda, Total, Parcialidad */}
                                <Row className="g-3 align-items-end">
                                    <Col xs={6} md={4}>
                                        <div className="text-muted small">Moneda DR</div>
                                        <div className="fw-bold" style={{ fontSize: '0.9rem' }}>{factura?.vMoneda || '-'}</div>
                                    </Col>
                                    <Col xs={6} md={4}>
                                        <div className="text-muted small">Total Factura</div>
                                        <div className="fw-bold text-success" style={{ fontSize: '0.9rem' }}>
                                            {factura?.dTotal ? `$${Number(factura.dTotal).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                                        </div>
                                    </Col>
                                    <Col xs={12} md={4}>
                                        <Form.Group>
                                            <Form.Label className="text-muted small mb-0">Parcialidad</Form.Label>
                                            <Form.Control
                                                size="sm"
                                                type="number"
                                                step="1"
                                                {...registerField('iNumeroParcialidad')}
                                            />
                                            {errNumeroParcialidad && <div className="text-danger small mt-1">{errNumeroParcialidad}</div>}
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>

                            {/* Renglón 4: Saldos */}
                            <Row className="g-2 mb-3">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="text-muted small mb-1">Saldo Anterior</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="number"
                                            step="0.01"
                                            className={isSaldoAnteriorMayorTotal ? 'border-warning' : ''}
                                            {...dSaldoAnteriorProps}
                                            onChange={(e) => {
                                                dSaldoAnteriorProps.onChange(e);
                                                handleSaldosChange(index);
                                            }}
                                        />
                                        {errSaldoAnterior && <div className="text-danger small mt-1">{errSaldoAnterior}</div>}
                                        {isSaldoAnteriorMayorTotal && !errSaldoAnterior && (
                                            <div className="text-warning small mt-1">
                                                <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                                                Mayor al total de la factura
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="text-muted small mb-1">Importe del Pago</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="number"
                                            step="0.01"
                                            className={isImportePagoMayorMonto ? 'border-warning' : ''}
                                            {...dImportePagoProps}
                                            onChange={(e) => {
                                                dImportePagoProps.onChange(e);
                                                handleSaldosChange(index);
                                            }}
                                        />
                                        {errImportePago && <div className="text-danger small mt-1">{errImportePago}</div>}
                                        {isImportePagoMayorMonto && !errImportePago && (
                                            <div className="text-warning small mt-1">
                                                <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                                                Mayor al monto del pago
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="text-muted small mb-1">Saldo Insoluto</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="number"
                                            step="0.01"
                                            className={isSaldoInsolutoNegativo ? 'border-warning text-warning' : ''}
                                            {...registerField('dSaldoInsoluto')}
                                            readOnly
                                        />
                                        {errSaldoInsoluto && <div className="text-danger small mt-1">{errSaldoInsoluto}</div>}
                                        {isSaldoInsolutoNegativo && !errSaldoInsoluto && (
                                            <div className="text-warning small mt-1">
                                                <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                                                Saldo negativo
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* Renglón 5: Objeto Impuesto y Equivalencia DR */}
                            <Row className="g-2 mb-3 align-items-start">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="text-muted small mb-1">Objeto Impuesto DR</Form.Label>
                                        <Form.Select
                                            size="sm"
                                            {...registerField('vObjetoImpDR')}
                                            disabled={true}
                                        >
                                            <option value="">Seleccione...</option>
                                            <option value="01">01 - No objeto de impuesto</option>
                                            <option value="02">02 - Sí objeto de impuesto</option>
                                            <option value="03">03 - Sí objeto del impuesto y no obligado al desglose</option>
                                        </Form.Select>
                                        {errObjetoImpDR && <div className="text-danger small mt-1">{errObjetoImpDR}</div>}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="text-muted small mb-1">Equivalencia DR</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="number"
                                            step="0.000001"
                                            {...registerField('dEquivalenciaDR')}
                                            disabled={!isDiferenteMoneda}
                                        />
                                        {errEquivalenciaDR && <div className="text-danger small mt-1">{errEquivalenciaDR}</div>}
                                    </Form.Group>
                                </Col>
                            </Row>

                            {isDiferenteMoneda && (
                                <Row className="mb-3">
                                    <Col xs={12}>
                                        <div className="alert alert-warning py-2 mb-0" style={{ fontSize: '0.85rem' }}>
                                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                            <strong>Atención:</strong> La moneda del documento relacionado ({monedaFactura}) es diferente a la moneda de pago ({monedaPago}). 
                                            Por favor ingresa la <strong>Equivalencia DR</strong> correspondiente.
                                        </div>
                                    </Col>
                                </Row>
                            )}

                            {/* Renglón 6: Tabla de impuestos */}
                            {currentObjetoImpDR === '02' && (impuestosTrasladados.length > 0 || impuestosRetenidos.length > 0) && (
                                <Row className="g-2">
                                    <Col xs={12}>
                                        <div className="table-responsive">
                                            <table className="table table-sm table-bordered" style={{ fontSize: '0.85rem' }}>
                                                <thead className="bg-light">
                                                    <tr>
                                                        <th>Tipo</th>
                                                        <th>Impuesto</th>
                                                        <th>Factor</th>
                                                        <th>Tasa/Cuota</th>
                                                        <th>Base DR</th>
                                                        <th>Importe DR</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {impuestosTrasladados.map((imp, i) => {
                                                        const proportion = (currentImportePago > 0 && factura?.dTotal > 0) ? (currentImportePago / parseFloat(factura.dTotal)) : 0;
                                                        const baseDR = (parseFloat(imp.BaseOriginal) || 0) * proportion;
                                                        const importeDR = (parseFloat(imp.ImporteOriginal) || 0) * proportion;
                                                        return (
                                                            <tr key={`traslado-${i}`}>
                                                                <td>Traslado</td>
                                                                <td>{imp.ImpuestoDR}</td>
                                                                <td>{imp.TipoFactorDR}</td>
                                                                <td>{imp.TasaOCuotaDR}</td>
                                                                <td className="text-end text-secondary">${baseDR.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}</td>
                                                                <td className="text-end text-success">${importeDR.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                    {impuestosRetenidos.map((imp, i) => {
                                                        const proportion = (currentImportePago > 0 && factura?.dTotal > 0) ? (currentImportePago / parseFloat(factura.dTotal)) : 0;
                                                        const baseDR = (parseFloat(imp.BaseOriginal) || 0) * proportion;
                                                        const importeDR = (parseFloat(imp.ImporteOriginal) || 0) * proportion;
                                                        return (
                                                            <tr key={`retenido-${i}`}>
                                                                <td>Retención</td>
                                                                <td>{imp.ImpuestoDR}</td>
                                                                <td>{imp.TipoFactorDR}</td>
                                                                <td>{imp.TasaOCuotaDR}</td>
                                                                <td className="text-end text-secondary">${baseDR.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}</td>
                                                                <td className="text-end text-danger">-${importeDR.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </Col>
                                </Row>
                            )}
                        </div>
                    );
                })
            )}
        </>
    );
};

DocumentoRelacionado.propTypes = {
    register: PropTypes.func.isRequired,
    getValues: PropTypes.func,
    setValue: PropTypes.func,
    errors: PropTypes.object.isRequired,
    selectedFacturas: PropTypes.array.isRequired,
    setSelectedFacturas: PropTypes.func.isRequired,
    impuestosFacturas: PropTypes.object,
    onAddFactura: PropTypes.func.isRequired,
    hasFacturasPPD: PropTypes.bool.isRequired
};

export default DocumentoRelacionado;
